"use client";

import { useMemo, useRef, useState, useEffect, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { PanchangCityProvider, usePanchangCity } from "@/contexts/PanchangCity";
import { CITY_MAP } from "@/lib/cities-data";
import { getPanchang } from "@/lib/api/panchang";

const PANCHANG_CONTEXT_STRIP_HEIGHT = 34;

interface PlaceCity {
  name: string;
  state: string;
  latitude: number;
  longitude: number;
  timezone: string;
}

// Module-level cache — fetched once, reused across re-renders.
let _citiesPromise: Promise<PlaceCity[]> | null = null;
function loadCities(): Promise<PlaceCity[]> {
  if (_citiesPromise) return _citiesPromise;
  _citiesPromise = fetch("/data/place-cities.json")
    .then((r) => (r.ok ? r.json() : []))
    .catch(() => []);
  return _citiesPromise;
}

function toSlug(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

function filterCities(cities: PlaceCity[], query: string): PlaceCity[] {
  if (!query.trim()) return cities.slice(0, 8);
  const q = query.toLowerCase();
  const prefix: PlaceCity[] = [];
  const substr: PlaceCity[] = [];
  for (const c of cities) {
    if (c.name.toLowerCase().startsWith(q)) {
      prefix.push(c);
      if (prefix.length === 8) break;
    } else if (`${c.name} ${c.state}`.toLowerCase().includes(q)) {
      substr.push(c);
    }
  }
  return [...prefix, ...substr].slice(0, 8);
}

function todayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function CityPicker() {
  const { city, setCity } = usePanchangCity();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [allCities, setAllCities] = useState<PlaceCity[]>([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Lazy-load on first open.
  useEffect(() => {
    if (!open || allCities.length > 0) return;
    loadCities()
      .then(setAllCities)
      .catch(() => {});
  }, [open, allCities.length]);

  // Focus input when opened.
  useEffect(() => {
    if (open) {
      const t = setTimeout(() => inputRef.current?.focus(), 10);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Close on outside click.
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const results = useMemo(() => filterCities(allCities, query), [allCities, query]);

  // Same disambiguation logic as cities-server.ts — needed so the slug stored in
  // localStorage matches what lookupCityBySlug() expects for duplicate city names.
  const slugCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const c of allCities) {
      const s = toSlug(c.name);
      counts[s] = (counts[s] ?? 0) + 1;
    }
    return counts;
  }, [allCities]);

  function handleSelect(c: PlaceCity) {
    const plainSlug = toSlug(c.name);
    // Curated cities always use plain slugs and are in CITY_MAP.
    const curated = CITY_MAP[plainSlug];
    if (curated) {
      setCity(curated);
      setOpen(false);
      return;
    }
    // Non-curated: disambiguate duplicate names with 3-char state suffix.
    const slug =
      (slugCounts[plainSlug] ?? 0) > 1 ? `${plainSlug}-${toSlug(c.state).slice(0, 3)}` : plainSlug;
    setCity({
      slug,
      name: c.name,
      state: c.state,
      latitude: c.latitude,
      longitude: c.longitude,
      timezone: c.timezone,
    });
    setOpen(false);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((p) => Math.min(p + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((p) => Math.max(p - 1, 0));
    } else if (e.key === "Enter" && activeIndex >= 0) {
      e.preventDefault();
      handleSelect(results[activeIndex]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <button
        onClick={() => {
          setOpen((o) => {
            const nextOpen = !o;
            if (nextOpen) {
              setQuery("");
              setActiveIndex(-1);
            }
            return nextOpen;
          });
        }}
        className="flex items-center gap-1 text-[11px] font-medium shrink-0"
        style={{
          color: "var(--ish-dark)",
          background: "none",
          border: "none",
          cursor: "pointer",
          padding: 0,
        }}
        aria-label="Change city"
        aria-expanded={open}
      >
        {city.name}
        <span style={{ fontSize: 8, opacity: 0.5 }}>▾</span>
      </button>

      {open && (
        <CityDropdown
          inputRef={inputRef}
          query={query}
          results={results}
          allCities={allCities}
          activeIndex={activeIndex}
          onQueryChange={(value) => {
            setQuery(value);
            setActiveIndex(-1);
          }}
          onKeyDown={handleKeyDown}
          onSelect={handleSelect}
          onActiveIndexChange={setActiveIndex}
        />
      )}
    </div>
  );
}

function CityDropdown({
  inputRef,
  query,
  results,
  allCities,
  activeIndex,
  onQueryChange,
  onKeyDown,
  onSelect,
  onActiveIndexChange,
}: {
  inputRef: React.RefObject<HTMLInputElement | null>;
  query: string;
  results: PlaceCity[];
  allCities: PlaceCity[];
  activeIndex: number;
  onQueryChange: (value: string) => void;
  onKeyDown: (event: React.KeyboardEvent) => void;
  onSelect: (city: PlaceCity) => void;
  onActiveIndexChange: (index: number) => void;
}) {
  return (
    <div
      style={{
        position: "absolute",
        top: "calc(100% + 8px)",
        right: 0,
        width: 260,
        background: "#fff",
        border: "1px solid var(--ish-border)",
        borderRadius: 8,
        boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
        zIndex: 50,
        overflow: "hidden",
      }}
    >
      <CitySearchInput
        inputRef={inputRef}
        query={query}
        onQueryChange={onQueryChange}
        onKeyDown={onKeyDown}
      />
      <CityResultList
        results={results}
        allCities={allCities}
        activeIndex={activeIndex}
        onSelect={onSelect}
        onActiveIndexChange={onActiveIndexChange}
      />
    </div>
  );
}

function CitySearchInput({
  inputRef,
  query,
  onQueryChange,
  onKeyDown,
}: {
  inputRef: React.RefObject<HTMLInputElement | null>;
  query: string;
  onQueryChange: (value: string) => void;
  onKeyDown: (event: React.KeyboardEvent) => void;
}) {
  return (
    <input
      ref={inputRef}
      type="text"
      value={query}
      onChange={(event) => onQueryChange(event.target.value)}
      onKeyDown={onKeyDown}
      placeholder="Search city…"
      style={{
        width: "100%",
        padding: "8px 12px",
        fontSize: 13,
        border: "none",
        borderBottom: "1px solid var(--ish-border)",
        outline: "none",
        boxSizing: "border-box",
        color: "var(--ish-dark)",
      }}
    />
  );
}

function CityResultList({
  results,
  allCities,
  activeIndex,
  onSelect,
  onActiveIndexChange,
}: {
  results: PlaceCity[];
  allCities: PlaceCity[];
  activeIndex: number;
  onSelect: (city: PlaceCity) => void;
  onActiveIndexChange: (index: number) => void;
}) {
  if (results.length === 0) {
    return (
      <div
        style={{
          padding: "12px",
          fontSize: 12,
          color: "var(--ish-text-faint)",
          textAlign: "center",
        }}
      >
        {allCities.length === 0 ? "Loading cities…" : "No results"}
      </div>
    );
  }
  return (
    <ul style={{ margin: 0, padding: 0, listStyle: "none", maxHeight: 240, overflowY: "auto" }}>
      {results.map((city, index) => (
        <CityResultOption
          key={`${city.name}-${city.state}`}
          city={city}
          index={index}
          isActive={index === activeIndex}
          isLast={index === results.length - 1}
          onSelect={onSelect}
          onActiveIndexChange={onActiveIndexChange}
        />
      ))}
    </ul>
  );
}

function CityResultOption({
  city,
  index,
  isActive,
  isLast,
  onSelect,
  onActiveIndexChange,
}: {
  city: PlaceCity;
  index: number;
  isActive: boolean;
  isLast: boolean;
  onSelect: (city: PlaceCity) => void;
  onActiveIndexChange: (index: number) => void;
}) {
  return (
    <li
      onMouseDown={() => onSelect(city)}
      onMouseEnter={() => onActiveIndexChange(index)}
      style={{
        padding: "8px 12px",
        cursor: "pointer",
        fontSize: 13,
        color: "var(--ish-dark)",
        backgroundColor: isActive ? "var(--ish-accent-light)" : "transparent",
        borderBottom: isLast ? "none" : "1px solid var(--ish-border-light, #f0ede9)",
      }}
    >
      <span style={{ fontWeight: 500 }}>{city.name}</span>
      <span style={{ color: "var(--ish-text-faint)", marginLeft: 6, fontSize: 11 }}>
        {city.state}
      </span>
    </li>
  );
}

function PanchangContextStripContent() {
  const { city } = usePanchangCity();
  const dateStr = useMemo(() => todayStr(), []);

  const { data } = useQuery({
    queryKey: ["panchang-strip", city.slug, dateStr],
    queryFn: () =>
      getPanchang({
        date: dateStr,
        latitude: city.latitude,
        longitude: city.longitude,
        tz_offset: 5.5,
      }),
    staleTime: 30 * 60 * 1000,
    retry: 1,
  });

  const vara = data?.vara.name ?? "–";
  const tithiName = data?.tithi.name ?? "–";
  // Backend returns "Shukla" / "Krishna" capitalized; case-insensitive
  // compare so the (S/K) badge stays correct (was always rendering "K").
  const paksha = data?.tithi.paksha?.toLowerCase() === "shukla" ? "S" : "K";
  const nakshatra = data?.nakshatra.name ?? "–";
  const sunrise = data?.sunrise ?? "–";

  return (
    <div
      className="mx-auto max-w-6xl px-4 flex items-center justify-between gap-4"
      style={{ height: `${PANCHANG_CONTEXT_STRIP_HEIGHT}px` }}
    >
      {/* Panchang snapshot */}
      <div
        className="flex items-center gap-2.5 min-w-0 overflow-hidden text-[11px]"
        style={{ color: "var(--ish-text-muted)" }}
      >
        <span className="font-semibold shrink-0" style={{ color: "var(--ish-dark)" }}>
          {vara}
        </span>
        <span className="opacity-25 shrink-0">·</span>
        <span className="shrink-0">
          <span className="opacity-60">Tithi </span>
          <span className="font-medium" style={{ color: "var(--ish-dark)" }}>
            {tithiName}
          </span>
          {data && <span className="ml-1 opacity-50 text-[10px]">({paksha})</span>}
        </span>
        <span className="opacity-25 shrink-0 hidden md:inline">·</span>
        <span className="hidden md:inline shrink-0">
          <span className="opacity-60">Nakshatra </span>
          <span className="font-medium" style={{ color: "var(--ish-dark)" }}>
            {nakshatra}
          </span>
        </span>
        <span className="opacity-25 shrink-0 hidden lg:inline">·</span>
        <span className="hidden lg:inline shrink-0">
          <span className="opacity-60">Sunrise </span>
          <span className="font-medium" style={{ color: "var(--ish-dark)" }}>
            {sunrise}
          </span>
        </span>
      </div>

      {/* City picker — lazy-loads 3,776 cities on first open */}
      <CityPicker />
    </div>
  );
}

function PanchangContextStripSkeleton() {
  return (
    <div
      aria-hidden="true"
      className="mx-auto max-w-6xl px-4 flex items-center justify-between gap-4"
      style={{ height: `${PANCHANG_CONTEXT_STRIP_HEIGHT}px` }}
    >
      <div
        className="flex items-center gap-2.5 min-w-0 overflow-hidden text-[11px]"
        style={{ color: "var(--ish-text-muted)" }}
      >
        <span className="font-semibold shrink-0" style={{ color: "var(--ish-dark)" }}>
          Panchang
        </span>
        <span className="opacity-25 shrink-0">·</span>
        <span className="shrink-0 opacity-60">Loading tithi</span>
        <span className="opacity-25 shrink-0 hidden md:inline">·</span>
        <span className="hidden md:inline shrink-0 opacity-60">Loading nakshatra</span>
      </div>
      <span className="text-[11px] font-medium shrink-0" style={{ color: "var(--ish-dark)" }}>
        New Delhi
      </span>
    </div>
  );
}

function PanchangContextStripShell({ children }: { children: ReactNode }) {
  return (
    <div
      className="sticky z-30 border-b hidden sm:block"
      style={{
        top: "52px",
        background: "var(--ish-accent-light)",
        borderColor: "var(--ish-border)",
        minHeight: `${PANCHANG_CONTEXT_STRIP_HEIGHT}px`,
      }}
    >
      {children}
    </div>
  );
}

function useDesktopPanchangStripEnabled() {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const media = window.matchMedia("(min-width: 640px)");
    const update = () => setEnabled(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  return enabled;
}

export default function PanchangContextStrip() {
  const enabled = useDesktopPanchangStripEnabled();

  return (
    <PanchangContextStripShell>
      {enabled ? (
        <PanchangCityProvider>
          <PanchangContextStripContent />
        </PanchangCityProvider>
      ) : (
        <PanchangContextStripSkeleton />
      )}
    </PanchangContextStripShell>
  );
}
